import React, { useState, useEffect, useReducer, useRef, useId } from 'react';
import { useTranslations } from '../translations';
import { fetchActiveLegalDocument } from '../services/legalDocuments';
import { isAnalyticsConfigured } from '../services/analytics';
import {
  OPEN_COOKIE_PREFERENCES_EVENT,
  getAnalyticsConsent,
  hasCurrentCookieNotice,
  isConsentPolicyKnown,
  saveAnalyticsConsent,
  saveCookieNoticeAcknowledgment,
  setConsentPolicyVersion,
  subscribeAnalyticsConsent,
} from '../services/analyticsConsent';
import './CookieConsent.css';

interface CookieConsentProps {
  onNavigateToCookies?: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ onNavigateToCookies }) => {
  const t = useTranslations();
  const titleId = useId();
  const [policyResolved, setPolicyResolved] = useState(false);
  const [reopened, setReopened] = useState(false);
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  const openerRef = useRef<HTMLElement | null>(null);
  const firstActionRef = useRef<HTMLButtonElement>(null);
  const customizeRef = useRef<HTMLButtonElement>(null);
  const analyticsToggleRef = useRef<HTMLInputElement>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsDraft, setAnalyticsDraft] = useState(false);
  const analyticsToggleId = useId();
  // Without a measurement ID there is no analytics to choose; keep the essential-only notice.
  const analyticsAvailable = isAnalyticsConfigured();

  useEffect(() => {
    let active = true;
    // Only a verified policy version can back a choice; on failure analytics stays off and no banner shows.
    const loadPolicy = () => fetchActiveLegalDocument('COOKIES')
      .then((document) => {
        if (!active) return;
        setConsentPolicyVersion(document.documentVersion);
        setPolicyResolved(true);
      })
      .catch(() => undefined);
    loadPolicy();
    const unsubscribe = subscribeAnalyticsConsent(rerender);
    const handleOpen = () => {
      openerRef.current = document.activeElement as HTMLElement | null;
      setReopened(true);
      if (!isConsentPolicyKnown()) loadPolicy();
    };
    window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, handleOpen);
    return () => {
      active = false;
      unsubscribe();
      window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, handleOpen);
    };
  }, []);

  const consent = analyticsAvailable ? getAnalyticsConsent() : null;
  const needsChoice = policyResolved
    && (analyticsAvailable ? consent === null : !hasCurrentCookieNotice());
  const visible = needsChoice || (reopened && policyResolved);

  useEffect(() => {
    if (reopened && policyResolved) firstActionRef.current?.focus();
  }, [reopened, policyResolved]);

  // Move focus between the summary and details views only after the user switches them.
  const detailsToggledRef = useRef(false);
  useEffect(() => {
    if (!detailsToggledRef.current) return;
    (showDetails ? analyticsToggleRef : customizeRef).current?.focus();
  }, [showDetails]);

  const toggleDetails = (open: boolean) => {
    if (open) setAnalyticsDraft(consent === true);
    detailsToggledRef.current = true;
    setShowDetails(open);
  };

  const close = () => {
    detailsToggledRef.current = false;
    setShowDetails(false);
    setReopened(false);
    openerRef.current?.focus();
    openerRef.current = null;
  };

  const choose = (granted: boolean) => {
    if (analyticsAvailable) {
      saveAnalyticsConsent(granted);
    } else {
      saveCookieNoticeAcknowledgment();
    }
    close();
  };

  if (!visible) {
    return null;
  }

  return (
    <div
      className="cookie-consent-modal"
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !needsChoice) close();
      }}
    >
      <h3 id={titleId} className="cookie-consent-title">{t.cookieConsent.title}</h3>
      {showDetails ? (
        <>
          <div className="cookie-option">
            <div className="cookie-option-header">
              <h4 className="cookie-option-title">{t.cookieConsent.necessaryTitle}</h4>
              <span className="cookie-option-badge">{t.cookieConsent.alwaysActive}</span>
            </div>
            <p className="cookie-option-description">{t.cookieConsent.necessaryDescription}</p>
          </div>
          <div className="cookie-option">
            <div className="cookie-option-header">
              <h4 className="cookie-option-title">{t.cookieConsent.analyticsTitle}</h4>
              <label className="cookie-checkbox" htmlFor={analyticsToggleId}>
                <input
                  ref={analyticsToggleRef}
                  id={analyticsToggleId}
                  type="checkbox"
                  checked={analyticsDraft}
                  onChange={(event) => setAnalyticsDraft(event.target.checked)}
                />
                <span className="cookie-checkbox-label">{t.cookieConsent.allowAnalytics}</span>
              </label>
            </div>
            <p className="cookie-option-description">{t.cookieConsent.analyticsOptionDescription}</p>
          </div>
          <div className="cookie-consent-actions">
            <button type="button" className="cookie-btn cookie-btn-back" onClick={() => toggleDetails(false)}>
              {t.cookieConsent.back}
            </button>
            <button type="button" className="cookie-btn cookie-btn-save" onClick={() => choose(analyticsDraft)}>
              {t.cookieConsent.save}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="cookie-consent-description">
            {analyticsAvailable ? t.cookieConsent.analyticsDescription : t.cookieConsent.description}
          </p>
          {analyticsAvailable && consent !== null && (
            <p className="cookie-consent-current">
              {consent ? t.cookieConsent.currentChoiceGranted : t.cookieConsent.currentChoiceDenied}
            </p>
          )}
          <p className="cookie-consent-policy">
            {t.cookieConsent.policyLink}{' '}
            <button
              type="button"
              className="cookie-consent-policy-link"
              onClick={() => onNavigateToCookies?.()}
            >
              {t.cookieConsent.policyLinkText}
            </button>.
          </p>
          <div className="cookie-consent-actions">
            {analyticsAvailable ? (
              <>
                <button ref={firstActionRef} type="button" className="cookie-btn cookie-btn-accept" onClick={() => choose(false)}>
                  {t.cookieConsent.declineAnalytics}
                </button>
                <button type="button" className="cookie-btn cookie-btn-accept" onClick={() => choose(true)}>
                  {t.cookieConsent.acceptAnalytics}
                </button>
                <button ref={customizeRef} type="button" className="cookie-btn cookie-btn-customize" onClick={() => toggleDetails(true)}>
                  {t.cookieConsent.customize}
                </button>
              </>
            ) : (
              <button ref={firstActionRef} type="button" className="cookie-btn cookie-btn-accept" onClick={() => choose(false)}>
                {t.cookieConsent.acceptAll}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CookieConsent;
